<!-- src/components/dashboard/bookings/RentToOwnPanel.vue -->
<!-- FT-173 STEP-15 (REQ-12): панель договора выкупа в карточке договорного
     бронирования — заменяет блок цены и статуса оплаты (BookingCard.vue) при
     booking.rent_to_own === true. Без initialSection запрашивает GET /rent_to_own/contracts по
     booking_id (REQ-17) и различает 5 исходов:
       - ровно один договор в ответе → панель с номером-ссылкой, чипом
         дисциплины, следующим платежом, просрочкой и прогрессом;
       - пустой список → плашка «Бронирование по договору выкупа» без
         данных договора, без кнопки;
       - paywall / 403 → та же плашка с «Принять платёж» (решение USER D4,
         FM-05: штатный PaymentDialog, allocate не зовётся);
       - сетевая ошибка (код не распознан parseRentToOwnError — нет ответа,
         500 без кода и т.п.) → та же плашка и кнопка «Повторить».
     Загрузка — та же плашка с индикатором (custom.loading_table).
     FT-173 STEP-16 (REQ-08): после КАЖДОЙ попытки load() эмитит
     contract-loaded(contract|null) — BookingCard решает по нему судьбу
     ?payment=1 (onRentToOwnContractLoaded). У активного договора — кнопка
     «Принять платёж» (эмитит accept-payment, BookingCard открывает штатный
     PaymentDialog через paymentStart()). handlePaymentSaved() дергается
     BookingCard'ом через ref после payment-saved: allocate + перечитать
     договор; при ошибке allocate — текст и «Повторить раскладку» (повторяет
     только allocate, платёж заново не шлёт — сверка раз в 5 минут всё равно
     разложит его, FM-07).
     REQ-21 (решение USER 2026-09-25): только базовые компоненты Vuetify 2 —
     панель встроена в поток BookingCard.vue (v-flex внутри v-card-text):
     классы типографики Vuetify (text--secondary, text-body-2), v-chip для
     дисциплины и состояния, v-btn для действий, пояснения к показателям,
     чипам и «Принять платёж» — v-tooltip (RentToOwnTip). axios через
     this.axios (VueAxios, интерсептор в main.js), моки в тестах ручным
     объектом (конвенция BookingAddonsPanel.spec.js:27-59). -->
<template>
  <div data-testid="rent-to-own-panel">
    <div v-if="isLoading" data-testid="rent-to-own-panel-loading" class="text--secondary">
      <v-progress-circular indeterminate color="primary" size="16" width="2" class="mr-1"></v-progress-circular>
      {{ $t("rent_to_own.booking_panel.placeholder") }} — {{ $t("custom.loading_table") }}
    </div>

    <template v-else-if="contract">
      <div data-testid="rent-to-own-panel-contract">
        <div class="d-flex align-center flex-wrap">
          <router-link
            class="subtitle-1 font-weight-bold mr-2"
            data-testid="rent-to-own-panel-number"
            :to="{ path: `/rent_to_own/contracts/${contract.id}` }"
          >
            {{ contract.number }}
          </router-link>
          <!-- Закрытый договор (terminated/completed/cancelled): оператор должен
               видеть, что договор уже закрыт, — чип состояния вместо чипа
               дисциплины; следующего платежа и просрочки у него нет. -->
          <rent-to-own-tip v-if="contractClosed" :text="rtoEnumLabel(LIFECYCLE_HINT_KEYS, contract.lifecycle)">
            <v-chip x-small outlined data-testid="rent-to-own-panel-lifecycle">
              {{ rtoEnumLabel(LIFECYCLE_KEYS, contract.lifecycle) }}
            </v-chip>
          </rent-to-own-tip>
          <rent-to-own-tip v-else :text="rtoEnumLabel(DISCIPLINE_HINT_KEYS, contract.discipline)">
            <v-chip x-small dark :color="disciplineColor(contract.discipline)" data-testid="rent-to-own-panel-discipline">
              {{ disciplineLabel(contract.discipline) }}
            </v-chip>
          </rent-to-own-tip>
        </div>

        <template v-if="!contractClosed">
          <div class="mt-1" data-testid="rent-to-own-panel-next-installment">
            <rent-to-own-tip icon :text="$t('rent_to_own.hints.header_next_installment')">{{ $t("rent_to_own.contract.header_next_installment") }}</rent-to-own-tip>:
            {{ nextInstallmentLabel }}
          </div>
          <div data-testid="rent-to-own-panel-overdue">
            <rent-to-own-tip icon :text="$t('rent_to_own.hints.header_overdue')">{{ $t("rent_to_own.contract.header_overdue") }}</rent-to-own-tip>:
            {{ overdueLabel }}
          </div>
        </template>
        <div :class="{ 'mt-1': contractClosed }" data-testid="rent-to-own-panel-progress">
          <rent-to-own-tip icon :text="$t('rent_to_own.hints.progress')">{{ $t("rent_to_own.contracts_list.col_progress") }}</rent-to-own-tip>:
          {{ progressLabel }}
        </div>

        <v-alert
          v-if="allocateError"
          dense
          outlined
          type="warning"
          class="mt-2 mb-0"
          data-testid="rent-to-own-panel-allocate-error"
        >
          {{ $t("rent_to_own.booking_panel.allocation_failed") }}
          <div class="text-right mt-1">
            <v-btn
              x-small
              outlined
              data-testid="rent-to-own-panel-retry-allocate"
              @click="retryAllocate"
            >
              {{ $t("rent_to_own.booking_panel.retry_allocation") }}
            </v-btn>
          </div>
        </v-alert>

        <v-divider class="mt-2 mb-2"></v-divider>

        <div class="d-flex align-center justify-space-between flex-wrap">
          <rent-to-own-tip v-if="contract.lifecycle === 'active'" :text="$t('rent_to_own.hints.action_accept_payment')" :focusable="false">
            <v-btn small color="success" data-testid="rent-to-own-panel-accept-payment" @click="acceptPayment">
              {{ $t("rent_to_own.booking_panel.accept_payment") }}
            </v-btn>
          </rent-to-own-tip>
          <router-link
            class="text-body-2"
            data-testid="rent-to-own-panel-back-link"
            :to="{ path: `/rent_to_own/contracts/${contract.id}` }"
          >
            ← {{ $t("rent_to_own.booking_panel.back_to_contract") }}
          </router-link>
        </div>
      </div>
    </template>

    <div v-else data-testid="rent-to-own-panel-placeholder" class="text--secondary">
      {{ $t(placeholderKey) }}
      <!-- Решение USER D4 (REQ-12, FM-05): деньги от клиента принимаются
           всегда — без доступа к модулю (paywall/403) плашка несёт «Принять
           платёж», штатный PaymentDialog карточки; раскладку сделает сверка. -->
      <div v-if="accessDenied" class="mt-2">
        <v-btn
          small
          color="success"
          data-testid="rent-to-own-panel-placeholder-accept-payment"
          @click="acceptPayment"
        >
          {{ $t("rent_to_own.booking_panel.accept_payment") }}
        </v-btn>
      </div>
      <v-btn
        v-if="networkError"
        x-small
        outlined
        class="ml-2"
        data-testid="rent-to-own-panel-retry"
        @click="load"
      >
        {{ $t("custom.retry") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { rentToOwnApi, parseRentToOwnError, isAccessDenied } from "@/api/rentToOwn";
import rentToOwnFormatMixin from "@/mixins/rentToOwnFormatMixin";
import RentToOwnTip from "@/components/dashboard/rent_to_own/RentToOwnTip.vue";
import { readInitialSection } from "@/components/dashboard/cards/initialSection";

// Карта вместо сборки ключа локали строкой — та же конвенция, что в
// ContractsList.vue/ContractPage.vue (dynamicKeys.spec.js требует статической
// карты под динамические ключи).
const DISCIPLINE_KEYS = Object.freeze({
  ok: "rent_to_own.contracts_list.discipline_ok",
  overdue: "rent_to_own.contracts_list.discipline_overdue",
  at_risk: "rent_to_own.contracts_list.discipline_at_risk",
});
// Подписи состояния договора — те же ключи, что на странице договора и в списке.
const LIFECYCLE_KEYS = Object.freeze({
  draft: "rent_to_own.contracts_list.lifecycle_draft",
  active: "rent_to_own.contracts_list.lifecycle_active",
  completed: "rent_to_own.contracts_list.lifecycle_completed",
  terminated: "rent_to_own.contracts_list.lifecycle_terminated",
  cancelled: "rent_to_own.contracts_list.lifecycle_cancelled",
});
const CLOSED_LIFECYCLES = Object.freeze(["terminated", "completed", "cancelled"]);
// Пояснения к чипам (замечание USER 2026-09-25) — те же ключи, что на
// странице договора и в списке.
const DISCIPLINE_HINT_KEYS = Object.freeze({
  ok: "rent_to_own.hints.discipline_ok",
  overdue: "rent_to_own.hints.discipline_overdue",
  at_risk: "rent_to_own.hints.discipline_at_risk",
});
const LIFECYCLE_HINT_KEYS = Object.freeze({
  draft: "rent_to_own.hints.lifecycle_draft",
  active: "rent_to_own.hints.lifecycle_active",
  completed: "rent_to_own.hints.lifecycle_completed",
  terminated: "rent_to_own.hints.lifecycle_terminated",
  cancelled: "rent_to_own.hints.lifecycle_cancelled",
});
// STEP-21b: цвет v-chip по дисциплине — просрочка красная, риск оранжевый,
// ok зелёный; та же тройка значений, что в DISCIPLINE_KEYS.
const DISCIPLINE_COLORS = Object.freeze({
  ok: "success",
  overdue: "error",
  at_risk: "warning",
});

export default {
  name: "RentToOwnPanel",
  components: { RentToOwnTip },
  mixins: [rentToOwnFormatMixin],
  props: {
    booking: { type: Object, required: true },
    initialSection: { type: Object, default: undefined },
    initialEntityId: { type: [Number, String], default: undefined },
  },
  data() {
    return {
      LIFECYCLE_KEYS,
      LIFECYCLE_HINT_KEYS,
      DISCIPLINE_HINT_KEYS,
      isLoading: false,
      contract: null,
      appliedInitialEntity: null,
      loadSeq: 0,
      // REQ-12: отличает "плашка без кнопки" (пусто/paywall/403) от "плашка
      // + Повторить" (сеть) — единственное различие в рендере этих исходов.
      networkError: false,
      // Решение USER D4: запрос договора ответил paywall или 403 — договор не
      // виден, но принять платёж по бронированию можно (кнопка на плашке).
      accessDenied: false,
      accessCode: null,
      // FT-173 STEP-16 (REQ-08, FM-07): true, когда payment-saved вызвал
      // allocate, а он ответил ошибкой — платёж уже сохранён сервером,
      // показываем «Повторить раскладку» вместо тихой потери.
      allocateError: false,
    };
  },
  computed: {
    initialData() { return [this.booking.id, this.initialSection, this.initialEntityId]; },
    // FT-194 SC-05: модуль выключен в филиале — плашка называет причину; прочие состояния — прежний текст.
    placeholderKey() {
      return this.accessCode === "module_disabled" ? "rent_to_own.errors.module_disabled" : "rent_to_own.booking_panel.placeholder";
    },
    contractClosed() {
      return !!this.contract && CLOSED_LIFECYCLES.includes(this.contract.lifecycle);
    },
    // H6: формат — rentToOwnFormat.js, общий с экранами модуля.
    nextInstallmentLabel() {
      return this.fmtNextInstallment(this.contract && this.contract.next_installment);
    },
    overdueLabel() {
      return this.fmtOverdue(this.contract);
    },
    progressLabel() {
      return this.contract ? this.fmtPercent(this.contract.progress_percent) : "—";
    },
  },
  created() {
    if (!this.applyInitialData()) this.load();
  },
  watch: {
    initialData(next, prev) {
      if (this.applyInitialData()) return;
      if (next[0] !== prev[0]) this.load();
    },
  },
  beforeDestroy() { ++this.loadSeq; },
  methods: {
    resetContractState() {
      this.isLoading = false;
      this.networkError = false;
      this.accessDenied = false;
      this.accessCode = null;
      this.allocateError = false;
      this.contract = null;
    },
    applyContractData(data) {
      const contracts = (data && data.contracts) || [];
      this.contract = contracts.length === 1 ? contracts[0] : null;
      this.$emit("contract-loaded", this.contract);
    },
    applyInitialData() {
      const section = readInitialSection(this.initialSection);
      if (!section.provided) return false;
      const entity = String(this.booking.id);
      if (this.initialEntityId !== undefined && String(this.initialEntityId) !== entity) {
        ++this.loadSeq;
        this.resetContractState();
        this.appliedInitialEntity = null;
        return true;
      }
      if (this.appliedInitialEntity === entity) return true;
      ++this.loadSeq;
      this.resetContractState();
      this.appliedInitialEntity = entity;
      this.networkError = section.status === "error";
      this.accessDenied = ["forbidden", "disabled"].includes(section.status);
      this.accessCode = section.status === "disabled" ? "module_disabled" : null;
      this.applyContractData(section.data);
      return true;
    },
    disciplineLabel(key) {
      return this.rtoEnumLabel(DISCIPLINE_KEYS, key);
    },
    disciplineColor(key) {
      return DISCIPLINE_COLORS[key] || "grey";
    },
    // REQ-12: запрос по booking_id. Ровно один договор в ответе → панель,
    // иначе (0 или больше 1 — по инварианту домена не бывает, но плашка не
    // требует различать) → плашка без данных. Код ошибки, распознанный
    // parseRentToOwnError (paywall/forbidden/…), — та же плашка без кнопки;
    // нераспознанный (сеть, 500 без кода) — плашка с "Повторить".
    load() {
      const bookingId = this.booking && this.booking.id;
      if (!bookingId) return Promise.resolve();
      const seq = ++this.loadSeq;
      this.resetContractState();
      this.isLoading = true;
      return rentToOwnApi
        .listContracts(this.axios, { booking_id: bookingId })
        .then((data) => {
          if (seq !== this.loadSeq) return;
          this.isLoading = false;
          // FT-173 STEP-16 (REQ-08, CTR-04): BookingCard's ?payment=1 gate
          // waits for this — fired after every attempt, found or not.
          this.applyContractData(data);
        })
        .catch((err) => {
          if (seq !== this.loadSeq) return;
          this.isLoading = false;
          this.contract = null;
          const parsed = parseRentToOwnError(err);
          this.networkError = !parsed;
          this.accessDenied = isAccessDenied(parsed);
          this.accessCode = parsed ? parsed.code : null;
          this.$emit("contract-loaded", null);
        });
    },
    // FT-173 STEP-16 (REQ-08): панель эмитит, BookingCard.paymentStart()
    // открывает штатный PaymentDialog карточки.
    acceptPayment() {
      this.$emit("accept-payment");
    },
    // FT-173 STEP-16 (REQ-08, FM-07): вызывается BookingCard'ом через ref
    // после payment-saved. Платёж уже сохранён сервером (save_count) — здесь
    // только раскладка по графику; при ошибке предлагаем повторить именно
    // allocate, не пересохранение платежа (сверка раз в 5 минут разложит его
    // и без ручного повтора).
    handlePaymentSaved() {
      // Без договора (в т.ч. D4 — нет доступа к модулю) allocate не зовём:
      // платёж сохранён, разложит его сверка раз в 5 минут.
      if (!this.contract) return Promise.resolve();
      // Контроль V1: allocate — только у действующего договора. У закрытого
      // (ряд оплаты открыт решением D3: возврат залога, сальдо) сервер
      // отвечает 409 invalid_transition — ложное «раскладка не выполнена» и
      // «Повторить» толкали бы оператора провести платёж повторно. Здесь
      // только перечитываем договор.
      if (this.contract.lifecycle !== "active") return this.load();
      this.allocateError = false;
      const contractId = this.contract.id;
      return rentToOwnApi
        .allocatePayment(this.axios, contractId)
        .then(() => this.load())
        .catch(() => {
          this.allocateError = true;
        });
    },
    retryAllocate() {
      return this.handlePaymentSaved();
    },
  },
};
</script>
